import { Env, Response as DomainResponse } from "@version2/domain-package";
import { detectEnvironment, parseOrigin } from "@zektec/environment-utils";
import { getCurrentUser } from "../firebase/auth";
import { normalizeApiError, validateJsonResponse } from "./utils";

const LOCAL_ENV = "local" as const;
type RuntimeEnv = Env | typeof LOCAL_ENV;

const LOCAL_HOSTNAMES = [
	"localhost",
	"127.0.0.1",
	"website.v2.local",
	"admin.v2.local",
];

const PRODUCTION_API_BASE_URL = "https://server.version2.io";
const STAGE_API_BASE_URL = "https://release-stage-server.version2.io";
const LOCAL_API_BASE_URL =
	process.env.REACT_APP_LOCAL_API_BASE_URL?.trim() ||
	"http://localhost:5001/version2-stage/us-central1/apiRestGateway";

const trimSlash = (value: string): string => value.replace(/\/+$/, "");

const parseEnvValue = (rawValue?: string): RuntimeEnv | undefined => {
	if (!rawValue) {
		return undefined;
	}

	switch (rawValue.trim().toLowerCase()) {
		case Env.PROD:
			return Env.PROD;
		case Env.STAGE:
			return Env.STAGE;
		case LOCAL_ENV:
			return LOCAL_ENV;
		default:
			return undefined;
	}
};

const detectEnvFromOrigin = (): RuntimeEnv | undefined => {
	const origin = window.location.origin;
	const parsedOrigin = parseOrigin(origin);
	const detected = detectEnvironment({
		origin,
		mode: import.meta.env.MODE,
		productionHostIndicators: ["version2.io"],
		stagingIndicators: ["release-stage", "version2.pages.dev"],
		localHostIndicators: LOCAL_HOSTNAMES,
	});

	if (detected === "staging") {
		return Env.STAGE;
	}

	if (
		detected === "local" ||
		detected === "development" ||
		detected === "test" ||
		parsedOrigin.isLocalhost
	) {
		return LOCAL_ENV;
	}

	if (detected === "production") {
		return Env.PROD;
	}

	return undefined;
};

const getApiBaseUrl = (): string => {
	const overrideUrl = process.env.REACT_APP_API_BASE_URL?.trim();

	if (overrideUrl) {
		return trimSlash(overrideUrl);
	}

	const selectedEnv =
		parseEnvValue(process.env.REACT_APP_ENV) ??
		detectEnvFromOrigin() ??
		Env.PROD;

	if (selectedEnv === LOCAL_ENV) {
		return trimSlash(LOCAL_API_BASE_URL);
	}

	if (selectedEnv === Env.STAGE) {
		return STAGE_API_BASE_URL;
	}

	return PRODUCTION_API_BASE_URL;
};

export const API_BASE_URL = getApiBaseUrl();

export interface ApiRequestOptions {
	method: "GET" | "POST" | "PUT" | "PATCH" | "DELETE";
	path: string;
	body?: unknown;
	requireAuth?: boolean;
	timeout?: number; // Timeout in milliseconds
}

// Default timeout for API requests (30 seconds)
const DEFAULT_TIMEOUT = 30000;

/**
 * Make a REST API request to the Version2 server with timeout support
 */
export const apiRequest = async (
	options: ApiRequestOptions
): Promise<DomainResponse> => {
	const { method, path, body, requireAuth = true, timeout = DEFAULT_TIMEOUT } = options;
	const url = `${API_BASE_URL}${path}`;

	const headers: Record<string, string> = {
		"Content-Type": "application/json",
	};

	// Add authorization header if required
	if (requireAuth) {
		const user = await getCurrentUser();
		if (user) {
			const token = await user.getIdToken();
			headers["Authorization"] = `Bearer ${token}`;
		}
	}

	// eslint-disable-next-line no-undef
	const requestOptions: RequestInit = {
		method,
		headers,
	};

	if (body && method !== "GET") {
		requestOptions.body = JSON.stringify(body);
	}

	// Create abort controller for timeout handling
	const controller = new AbortController();
	const timeoutId = setTimeout(() => controller.abort(), timeout);

	try {
		// Add signal to request options
		const requestOptionsWithSignal = {
			...requestOptions,
			signal: controller.signal
		};

		const response = await fetch(url, requestOptionsWithSignal);
		clearTimeout(timeoutId);

		// Handle rate limiting
		if (response.status === 429) {
			const retryAfter = response.headers.get("Retry-After");
			const waitTime = retryAfter ? `${retryAfter} seconds` : "a moment";
			throw new Error(`Too many requests. Please try again after ${waitTime}.`);
		}

		// Validate response is JSON
		validateJsonResponse(response);

		const data = await response.json();
		if (!response.ok) {
			throw normalizeApiError(
				data,
				`Request failed with status ${response.status}`
			);
		}
		return DomainResponse.fromJson(data);
	} catch (error) {
		clearTimeout(timeoutId);
		console.error("API request failed:", error);

		// Handle abort errors (timeout)
		if (error instanceof Error && error.name === 'AbortError') {
			throw new Error(`Request timeout after ${timeout}ms`);
		}

		// Re-throw with more context if available
		throw normalizeApiError(error);
	}
};

/**
 * Helper functions for common HTTP methods
 */
export const apiGet = (path: string, requireAuth = true) =>
	apiRequest({ method: "GET", path, requireAuth });

export const apiPost = (
	path: string,
	body?: unknown,
	requireAuth = true
) => apiRequest({ method: "POST", path, body, requireAuth });

export const apiPatch = (
	path: string,
	body?: unknown,
	requireAuth = true
) => apiRequest({ method: "PATCH", path, body, requireAuth });

export const apiDelete = (
	path: string,
	body?: unknown,
	requireAuth = true
) => apiRequest({ method: "DELETE", path, body, requireAuth });
